import { TimberItem, OCRResult } from '../types/bidding';
import { uploadBidDocument } from './firebase';
import { parseTimberListFromCSV, parseTimberListFromText } from './timberListParser';

/**
 * Get the Vision API key from Firestore settings
 */
async function getVisionApiKey(): Promise<string> {
  // In production, this should fetch from Firestore settings
  // For now, we'll use an environment variable or prompt user
  const apiKey = process.env.REACT_APP_VISION_API_KEY || localStorage.getItem('visionApiKey');
  
  if (!apiKey) {
    throw new Error('Vision API key not configured. Please add it in settings.');
  }
  
  return apiKey;
}

/**
 * Process a document file: upload to storage, run OCR if needed, parse timber list
 * @param bidId - The bid ID to associate the document with
 * @param file - The file to process (PDF, image, or CSV)
 * @returns OCR result with parsed timber items
 */
export async function processDocument(
  bidId: string,
  file: File
): Promise<OCRResult> {
  try {
    // Upload file to Firebase Storage
    const downloadUrl = await uploadBidDocument(bidId, file);
    
    // Determine file type and processing strategy
    const fileType = file.type;
    const fileName = file.name.toLowerCase();
    
    // If CSV, parse directly without OCR
    if (fileType === 'text/csv' || fileName.endsWith('.csv')) {
      const text = await file.text();
      const items = parseTimberListFromCSV(text);
      
      return {
        text,
        confidence: 1.0, // CSV is exact, no OCR uncertainty
        extractedItems: items,
        warnings: [],
      };
    }
    
    // For images and PDFs, use Google Cloud Vision API
    if (fileType.startsWith('image/') || fileType === 'application/pdf') {
      return await extractTimberListWithVision(downloadUrl);
    }
    
    throw new Error('Unsupported file type. Please upload CSV, PDF, or image files.');
  } catch (error) {
    console.error('Error processing document:', error);
    throw error;
  }
}

/**
 * Extract timber list from image or PDF using Google Cloud Vision API
 * @param imageUrl - Public URL of the image/PDF
 * @returns OCR result with parsed timber items
 */
async function extractTimberListWithVision(imageUrl: string): Promise<OCRResult> {
  try {
    const apiKey = await getVisionApiKey();
    
    const response = await fetch(
      `https://vision.googleapis.com/v1/images:annotate?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requests: [
            {
              image: {
                source: {
                  imageUri: imageUrl,
                },
              },
              features: [
                {
                  type: 'DOCUMENT_TEXT_DETECTION', // Better for structured documents
                  maxResults: 1,
                },
              ],
            },
          ],
        }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Vision API error: ${JSON.stringify(errorData)}`);
    }
    
    const data = await response.json();
    const annotations = data.responses[0];
    
    if (!annotations || !annotations.fullTextAnnotation) {
      throw new Error('No text detected in image. Please ensure the document is clear and legible.');
    }
    
    const extractedText = annotations.fullTextAnnotation.text;
    const confidence = annotations.fullTextAnnotation.pages?.[0]?.confidence || 0.8;
    
    // Parse the extracted text into timber items
    const items = parseTimberListFromText(extractedText);
    
    // Identify low-confidence items
    const warnings: string[] = [];
    items.forEach((item, index) => {
      if (item.confidence && item.confidence < 0.7) {
        warnings.push(`Item ${index + 1} (${item.name}) has low confidence. Please verify.`);
      }
    });
    
    return {
      text: extractedText,
      confidence,
      extractedItems: items,
      warnings,
    };
  } catch (error) {
    console.error('Error extracting text with Vision API:', error);
    throw error;
  }
}

/**
 * Extract timber list from a File object (client-side processing)
 * Used for CSV files and as fallback for images
 * @param file - The file to process
 * @returns OCR result with parsed timber items
 */
export async function extractTimberListFromFile(file: File): Promise<OCRResult> {
  const fileType = file.type;
  const fileName = file.name.toLowerCase();
  
  // CSV files - parse directly
  if (fileType === 'text/csv' || fileName.endsWith('.csv')) {
    const text = await file.text();
    const items = parseTimberListFromCSV(text);
    
    return {
      text,
      confidence: 1.0,
      extractedItems: items,
      warnings: [],
    };
  }
  
  // For images, we need to upload first to get a public URL
  throw new Error('Image files require upload to Firebase Storage before OCR processing.');
}

/**
 * Validate Vision API key
 * @param apiKey - The API key to validate
 * @returns True if valid, false otherwise
 */
export async function validateVisionApiKey(apiKey: string): Promise<boolean> {
  try {
    // Make a simple request to test the key
    const testImageUrl = 'https://storage.googleapis.com/cloud-samples-data/vision/ocr/sign.jpg';
    
    const response = await fetch(
      `https://vision.googleapis.com/v1/images:annotate?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requests: [
            {
              image: { source: { imageUri: testImageUrl } },
              features: [{ type: 'TEXT_DETECTION', maxResults: 1 }],
            },
          ],
        }),
      }
    );
    
    return response.ok;
  } catch (error) {
    return false;
  }
}

/**
 * Save Vision API key to localStorage (temporary solution)
 * In production, this should be saved to Firestore settings
 */
export function saveVisionApiKey(apiKey: string): void {
  localStorage.setItem('visionApiKey', apiKey);
}

/**
 * Get saved Vision API key
 */
export function getSavedVisionApiKey(): string | null {
  return localStorage.getItem('visionApiKey');
}
